import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { Newspaper, UploadCloud, Download, Trash2, FileText, X } from "lucide-react";
import { toast } from "sonner";

export default function NewslettersPanel({ can }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const editable = can("manage_newsletter");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/newsletters");
      setList(data || []);
    } catch {/* 401 handled globally */}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (n) => {
    if (!window.confirm(`Delete the ${n.month_label} newsletter? Parents will no longer see it.`)) return;
    try {
      await adminApi.delete(`/admin/newsletters/${n.id}`);
      toast.success("Newsletter removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="newsletters-panel">
      <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#648DE5]/15 text-[#648DE5] flex items-center justify-center">
            <Newspaper size={18} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Monthly newsletter</h2>
            <p className="text-sm text-[#4A5568] mt-0.5 max-w-2xl">Upload one PDF newsletter per month. Parents see the most recent one in their portal.</p>
          </div>
        </div>
        {editable && (
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
            data-testid="newsletter-upload-btn"
          >
            <UploadCloud size={14} /> Upload newsletter
          </button>
        )}
      </div>

      <div className="p-6 md:p-7">
        {loading ? (
          <div className="text-sm text-[#718096] py-6 text-center">Loading newsletters…</div>
        ) : list.length === 0 ? (
          <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
            <Newspaper size={28} className="mx-auto text-[#648DE5] mb-2" />
            <div className="text-[#0F1B47] font-medium">No newsletters yet</div>
            <p className="text-xs text-[#718096] mt-1">{editable ? "Click \"Upload newsletter\" to publish the first issue." : "Newsletters will appear here once an admin uploads them."}</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="newsletters-list">
            {list.map((n) => (
              <li key={n.id} className="bg-[#F5F5F0] rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`newsletter-row-${n.id}`}>
                <div className="w-12 h-12 rounded-xl bg-[#FDFDF9] text-[#648DE5] flex items-center justify-center flex-shrink-0">
                  <FileText size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0F1B47]">{n.title || n.month_label}</div>
                  <div className="text-xs text-[#4A5568] mt-0.5">{n.month_label} · {n.filename}</div>
                  <div className="text-[11px] text-[#718096] mt-0.5">Uploaded {new Date(n.uploaded_at).toLocaleString()} · {fmtSize(n.size)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/${n.id}.pdf`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1B47] px-3.5 py-2 rounded-full bg-[#E3EDF9] hover:bg-[#CFE0F0]"
                    data-testid={`newsletter-download-${n.id}`}
                  >
                    <Download size={12} /> PDF
                  </a>
                  {editable && (
                    <button onClick={() => remove(n)} className="text-red-600 hover:text-red-700 p-2" title="Delete newsletter">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showUpload && (
        <UploadNewsletterModal
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); load(); }}
        />
      )}
    </div>
  );
}

function UploadNewsletterModal({ onClose, onSaved }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [monthIso, setMonthIso] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a PDF to upload");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("month_iso", monthIso);
      await adminApi.post("/admin/newsletters", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Newsletter uploaded — parents will see it on their next visit.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-[28px] w-full max-w-md shadow-2xl overflow-hidden" data-testid="newsletter-upload-modal">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium">Upload newsletter</h3>
            <p className="text-xs text-[#4A5568] mt-0.5">PDF only · max 15 MB</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Month *">
            <input type="month" value={monthIso} onChange={(e) => setMonthIso(e.target.value)} className={inputCls} required data-testid="newsletter-month" />
          </Field>
          <Field label="Title (optional)">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. May 2026 · The Naavi Times" className={inputCls} data-testid="newsletter-title" />
          </Field>
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">PDF file *</span>
            <div
              onClick={() => fileRef.current?.click()}
              className="cursor-pointer rounded-2xl border-2 border-dashed border-[#0F1B47]/15 bg-[#F5F5F0] hover:bg-[#0F1B47]/5 px-5 py-8 text-center transition-colors"
              data-testid="newsletter-dropzone"
            >
              <UploadCloud size={22} className="mx-auto text-[#648DE5]" />
              <div className="text-sm font-semibold text-[#0F1B47] mt-2">{file ? file.name : "Click to choose a PDF"}</div>
              <div className="text-[11px] text-[#718096] mt-0.5">{file ? fmtSize(file.size) : "or drag & drop"}</div>
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="newsletter-file" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3">
          <button type="submit" disabled={busy || !file} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="newsletter-upload-confirm">
            {busy ? "Uploading…" : "Upload"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
